import React from 'react';

/**
 * ModalSidebarNav — the reusable left-rail navigation used inside large modals
 * (Settings, Help guide, …). Mirrors the operator dashboard sidebar exactly:
 * light-gray background, soft "blue-50 / blue-700" active state, gray hover —
 * no hard blue pills, no right border.
 *
 * @param {string} [title]
 * @param {Array<{ id: string, label: string, icon: React.ComponentType, danger?: boolean }>} tabs
 * @param {string} activeTab
 * @param {(id: string) => void} onTabChange
 */
const ModalSidebarNav = ({ title, tabs = [], activeTab, onTabChange, className = '' }) => (
  <div className={`hidden md:flex md:flex-col w-48 bg-gray-50 overflow-y-auto flex-shrink-0 rounded-tl-2xl ${className}`}>
    {title && (
      <div className="px-4 pt-5 pb-2">
        <h2 className="text-lg font-semibold text-gray-900 tracking-tight">{title}</h2>
      </div>
    )}
    <nav className="px-3 pb-3 pt-1 space-y-0.5">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            data-testid={`modal-nav-${tab.id}`}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm transition-all duration-150 ${
              isActive
                ? tab.danger
                  ? 'bg-red-50 text-red-700 font-medium'
                  : 'bg-blue-50 text-blue-700 font-medium'
                : tab.danger
                  ? 'text-red-600 hover:bg-gray-100'
                  : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
            <span className="truncate">{tab.label}</span>
            {tab.badge > 0 && (
              <span
                className={`ml-auto min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-semibold flex items-center justify-center ${
                  isActive ? 'bg-white text-blue-700' : 'bg-blue-600 text-white'
                }`}
                data-testid={`modal-nav-badge-${tab.id}`}
              >
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  </div>
);

export default ModalSidebarNav;
